import { createContext, FC, ReactNode, useMemo } from 'react';
import { ResponseType } from '../utils/type';
import { StatusType } from '../pages/activitySheet/ActivityTableRow';
import { useMultiplePermissions } from '../hooks/usePermissions';

export type ActivitySheetContextType = {
  updateVisitLevelNote: (
    progressNotesId: string,
    visitId: string,
    description: string,
  ) => void;
  updateNoteErrorMessage: (
    errorType: ResponseType,
    id: string,
    message: string,
  ) => void;
  activityStatus: StatusType | null;
  children: ReactNode;
  updateVisitStatus?: (
    activitySheetId: string,
    appointmentId: string,
    status: StatusType | null,
    updateParentStatus: boolean,
  ) => void;
  updateVisitOnHold?: (
    activitySheetId: string,
    visitId: string,
    onHold: boolean,
  ) => void;
  onChevronClick?: (expand: boolean, activitySheetId: string) => void;
  updateActivityStatus?: (
    activitySheetId: string,
    activityStatus: 'Open' | 'Closed' | 'Pending',
  ) => void;
  submitReviewedBillableHours?: () => void;
  submitPayrollActivities?: () => void;
  reload?: () => void;
  readPayrollDetails?: () => void;
  readBillingDetails?: () => void;
};

export const ActivitySheetContext = createContext<{
  context: ActivitySheetContextType | null;
  canBillingHoursEdit: boolean;
  canPayrollHoursEdit: boolean;
}>({ context: null, canBillingHoursEdit: false, canPayrollHoursEdit: false });

export const ActivitySheetContextProvider: FC<ActivitySheetContextType> = ({
  reload,
  children,
  updateVisitLevelNote,
  updateNoteErrorMessage,
  activityStatus,
  updateVisitStatus,
  updateVisitOnHold,
  onChevronClick,
  updateActivityStatus,
  submitReviewedBillableHours,
  submitPayrollActivities,
  readPayrollDetails,
  readBillingDetails,
}) => {
  const permissions = useMultiplePermissions([
    'backend.payroll_hours_ras',
    'backend.billing_hours_ras',
  ]);
  const canBillingHoursEdit = permissions['backend.billing_hours_ras'].canEdit;
  const canPayrollHoursEdit = permissions['backend.payroll_hours_ras'].canEdit;

  const context = useMemo(
    () => ({
      canBillingHoursEdit,
      canPayrollHoursEdit,
      reload,
      children,
      updateVisitLevelNote,
      updateNoteErrorMessage,
      activityStatus,
      updateVisitStatus,
      updateVisitOnHold,
      onChevronClick,
      updateActivityStatus,
      submitReviewedBillableHours,
      submitPayrollActivities,
      readPayrollDetails,
      readBillingDetails,
    }),
    [
      reload,
      canBillingHoursEdit,
      canPayrollHoursEdit,
      children,
      updateVisitLevelNote,
      updateNoteErrorMessage,
      activityStatus,
      updateVisitStatus,
      updateVisitOnHold,
      onChevronClick,
      updateActivityStatus,
      submitReviewedBillableHours,
      submitPayrollActivities,
      readPayrollDetails,
      readBillingDetails,
    ],
  );

  const providerValue = useMemo(
    () => ({ context, canBillingHoursEdit, canPayrollHoursEdit }),
    [context, canBillingHoursEdit, canPayrollHoursEdit],
  );

  return (
    <ActivitySheetContext.Provider value={providerValue}>
      {children}
    </ActivitySheetContext.Provider>
  );
};
